import { Formik } from "formik";
import { useContext, useState } from "react";
import DatePicker from "react-date-picker";
import Select from "react-select";
import * as yup from "yup";

import appConstants, {
  addDays,
  isValidDate,
  selectControlStyling,
  selectOptionControlStyling,
  subtractYears,
} from "../../../app-constants";
import appStyles from "../../../App.module.css";
import ScrollToFirstError from "../../../common-components/ScrollToFirstError";
import { IPortugueseInitialQuoteData, IPortuguesePurchasePathContext, IPQuoteForm } from "../../config/interface";
import { regions, validateAge } from "../../config/portuguese-Constant";
import { PortuguesePurchasePathContext } from "../../PortuguesePurchasePath";
import NextButton from "../buttons/NextButton";
import PPFooter from "../common/PPFooter";
import PPLoadingIndicator from "../common/PPLoadingIndicator";
import PPShowApiError from "../common/PPShowApiError";
import PPStepper from "../common/PPStepper";
import PPTooltipIcon from "../common/PPTooltipIcon";

const PPQuoteForm = () => {
  const {
    next,
    quoteData,
    setQuoteData,
    hostCountries,
    loader,
    setLoader,
    setPackages,
    setQuoteResult,
    updateCountryAndDestination,
    getPackages,
    getQuote,
  } = useContext(PortuguesePurchasePathContext) as IPortuguesePurchasePathContext;

  const mainApplicant = yup
    .date()
    .required("This field is required")
    .max(subtractYears(new Date(), 18), "Traveller must be at least 18 years old on policy start date.")
    .test("amtMainApplicant", "Invalid date entered.", (value: any) => {
      return isValidDate(value);
    })
    .when("start_date", (start_date: any, schema) => {
      if (start_date && start_date?.length && start_date[0]) {
        return schema.min(
          addDays(new Date(), -18263),
          "Traveller must not be older than 50 years on policy start date."
        );
      }
      return schema;
    });

  const validationSchema = yup.object().shape({
    host_country: yup.object().required(),
    currency: yup.object().required(),
    start_date: yup.date().required(),
    end_date: yup.date().required(),
    regions: yup.array().min(1, "At least one destination is required."),
    main_applicant: mainApplicant,
  });

  const initialFormValues: IPQuoteForm = {
    host_country: quoteData.host_country || null,
    currency: quoteData.currency,
    start_date: quoteData.start_date,
    end_date: quoteData.end_date,
    regions: quoteData.regions,
    main_applicant: quoteData.main_applicant,
  };

  const [hasError, setHasError] = useState<string | null>(null);

  const handleFormSubmit = async (values: IPQuoteForm) => {
    try {
      setLoader("quote");

      let _quoteData: IPortugueseInitialQuoteData = {
        ...quoteData,
        packages: null,
        applicantData: null,
      };

      _quoteData.host_country = values.host_country;
      _quoteData.currency = values.currency;
      _quoteData.start_date = values.start_date;
      _quoteData.end_date = values.end_date;
      _quoteData.regions = values.regions;
      _quoteData.main_applicant = values.main_applicant;

      const _packages = await getPackages(_quoteData);
      const _quoteResult = await getQuote(_quoteData);

      setPackages(() => _packages);
      setQuoteResult(_quoteResult);
      setQuoteData(_quoteData);
      next();
    } catch (e: any) {
      console.log(e);
      setHasError(e.message);
    } finally {
      setLoader(null);
    }
  };

  let ageConfirmation = null;

  return (
    <>
      {loader === "quote" && <PPLoadingIndicator />}
      <div className={`${appStyles.mainContainer} ${appStyles.W600} ${appStyles.p32}`}>
        <div className={appStyles.headerWrapper}>
          <div className={appStyles.pageTitle}>
            <h2 className={`${appStyles.colorPrimary}`}>Get a Quick Quote</h2>
          </div>
        </div>

        <PPStepper />

        <Formik
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            setFieldValue,
            handleSubmit,
            submitCount,
            isValid,
            validateForm,
          }) => (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await validateForm();
                handleSubmit();
              }}
            >
              <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
              <div className={`${appStyles.borderBox} ${appStyles.formBox}`}>
                <div className={appStyles.formWrapper}>
                  <div className={appStyles.pageSubTitle}>
                    <h3 className={appStyles.colorPrimary}>Personal Information</h3>
                  </div>
                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label>
                        What's your country of residence?*
                        <PPTooltipIcon
                          text={"The country in which you are currently a legal resident."}
                        ></PPTooltipIcon>
                      </label>
                      <div className={appStyles.formRow}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.host_country && errors?.host_country ? appStyles.hasError : ""
                          }`}
                        >
                          {hostCountries.length === 1 ? (
                            <input
                              type="text"
                              name="host_country"
                              id="host_country"
                              onBlur={handleBlur("host_country")}
                              value={values.host_country ? values.host_country?.name : ""}
                              className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                              disabled={true}
                            />
                          ) : (
                            <Select
                              isClearable={true}
                              isSearchable={true}
                              name="host_country"
                              id="host_country"
                              options={hostCountries || []}
                              isLoading={loader === "master_data"}
                              classNames={{
                                control: selectControlStyling,
                                option: selectOptionControlStyling,
                              }}
                              onBlur={handleBlur("host_country")}
                              onChange={(newValue) => {
                                setFieldValue("host_country", newValue);
                              }}
                              value={values.host_country}
                              getOptionValue={(option: any) => `${option["code"]}`}
                              getOptionLabel={(option: any) => `${option["name"]}`}
                              blurInputOnSelect={false}
                            />
                          )}

                          {touched?.host_country && errors?.host_country && (
                            <div className={appStyles.formError}>This field is required</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={appStyles.formBlock}>
                      <label>Currency?*</label>
                      <div className={appStyles.formRow}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.currency && errors?.currency ? appStyles.hasError : ""
                          }`}
                        >
                          <input
                            type="text"
                            name="currency"
                            id="currency"
                            value={values.currency ? values.currency?.label : ""}
                            onBlur={handleBlur("currency")}
                            className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple}`}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={appStyles.pageSubTitle}>
                    <h3 className={appStyles.colorPrimary}>Trip Information</h3>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label>
                        When do you want cover to start?*
                        <PPTooltipIcon text={"The date you want the policy to start."}></PPTooltipIcon>
                      </label>
                      <div
                        className={`${appStyles.formField} ${
                          touched?.start_date && errors?.start_date ? appStyles.hasError : ""
                        }`}
                      >
                        <DatePicker
                          calendarClassName={appStyles.bfpDatePickerCalendar}
                          className={`${appStyles.bfpDatePicker} ${appStyles.bfpDatePickerCalendarStartDate}`}
                          value={values.start_date}
                          format={appConstants.datePickerFormat}
                          minDate={addDays(new Date(), 1)}
                          maxDate={addDays(new Date(), 365)}
                          dayPlaceholder="dd"
                          monthPlaceholder="mm"
                          yearPlaceholder="yyyy"
                          id="start_date"
                          onChange={(value: any) => {
                            setFieldValue("start_date", value);
                            if (values?.end_date && new Date(value) > new Date(values?.end_date)) {
                              setFieldValue("end_date", addDays(value, 1));
                            }
                          }}
                          onBlur={handleBlur("start_date")}
                        />
                        {touched?.start_date && errors?.start_date && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>

                    <div className={appStyles.formBlock}>
                      <label>
                        When do you want cover to end?*
                        <PPTooltipIcon text={"The date you want the policy to end."}></PPTooltipIcon>
                      </label>
                      <div
                        className={`${appStyles.formField} ${
                          touched?.end_date && errors?.end_date ? appStyles.hasError : ""
                        }`}
                      >
                        <DatePicker
                          calendarClassName={appStyles.bfpDatePickerCalendar}
                          className={`${appStyles.bfpDatePicker} ${appStyles.bfpDatePickerCalendarEndDate}`}
                          format={appConstants.datePickerFormat}
                          value={values.end_date}
                          minDate={addDays(values.start_date || new Date(), 1)}
                          maxDate={addDays(values.start_date || new Date(), 365)}
                          dayPlaceholder="dd"
                          monthPlaceholder="mm"
                          yearPlaceholder="yyyy"
                          id="end_date"
                          onChange={(value) => {
                            setFieldValue("end_date", value);
                          }}
                          onBlur={handleBlur("end_date")}
                        />
                        {touched?.end_date && errors?.end_date && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label>
                        Where will you be travelling?*
                        <PPTooltipIcon text={"Please select one"}></PPTooltipIcon>
                      </label>
                      <div className={appStyles.formRow}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.regions && errors?.regions ? appStyles.hasError : ""
                          }`}
                        >
                          <Select
                            name="regions"
                            id="regions"
                            options={regions || []}
                            isLoading={loader === "master_data"}
                            classNames={{
                              control: selectControlStyling,
                            }}
                            value={values.regions}
                            onBlur={handleBlur("regions")}
                            onChange={(newValue: any) => {
                              setFieldValue("regions", [newValue]);
                              updateCountryAndDestination(values, newValue);
                            }}
                            getOptionValue={(option: any) => option.value}
                            getOptionLabel={(option: any) => option.label}
                            blurInputOnSelect={false}
                          />
                          {touched?.regions && errors?.regions && (
                            <div className={appStyles.formError}>This field is required</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={appStyles.pageSubTitle}>
                    <h3 className={appStyles.colorPrimary}>Traveller Information</h3>
                  </div>
                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label>
                        Please enter your date of birth*
                        <PPTooltipIcon text={"For lead applicant"}></PPTooltipIcon>
                      </label>
                      <div className={`${appStyles.formRow}`}>
                        <div
                          className={`${appStyles.formField} ${
                            touched?.main_applicant && errors?.main_applicant ? appStyles.hasError : ""
                          }`}
                        >
                          <DatePicker
                            calendarClassName={appStyles.bfpDatePickerCalendar}
                            className={appStyles.bfpDatePicker}
                            format={appConstants.datePickerFormat}
                            value={values.main_applicant}
                            dayPlaceholder="dd"
                            monthPlaceholder="mm"
                            yearPlaceholder="yyyy"
                            id="main_applicant"
                            maxDate={new Date()}
                            onChange={(value) => {
                              setFieldValue("main_applicant", value);
                            }}
                            disableCalendar={true}
                          />
                          {touched?.main_applicant && errors?.main_applicant && (
                            <div className={appStyles.formError}>{errors?.main_applicant}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      {(ageConfirmation = validateAge(values.main_applicant, [new Date()], values.start_date)) ? (
                        <div className={appStyles.disclaimerText}>
                          <span className={appStyles.infoIcon}></span>
                          <p>{ageConfirmation}</p>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className={appStyles.formRow}>
                <div
                  className={`${appStyles.buttonBlock} ${appStyles.buttonBlockNextPrev} ${appStyles.buttonBlockAlignEnd}`}
                >
                  <NextButton title="Get Quote" type="submit" />
                </div>
              </div>

              {hasError && <PPShowApiError />}
            </form>
          )}
        </Formik>

        <PPFooter />
      </div>
    </>
  );
};
export default PPQuoteForm;
